import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Create, GetEmployeeList } from "adminApi/GrandLoanApi/GrandLoanApi";
import { validateForm } from "utils/validation";
import { GrandLoanRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import AddGrandLoanForm from "./components/AddGrandLoanForm";
import fields from "../json/GrandLoan.json";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchAllData from "Hooks/useFetchAllData";

const buildPayload = (formValues) => ({
  employee_id: formValues.employee_id,
  permitted_by: formValues.permitted_by,
  loan_details: formValues.loan_details,
  approve_date: formValues.approve_date,
  repayment_from: formValues.repayment_from,
  total_amount: formValues.total_amount,
  interest_percentage: formValues.interest_percentage,
  installment_period_month: formValues.installment_period_month,
  status: formValues.status,
});

const AddGrandLoan = () => {
  const navigate = useNavigate();
  const { formdata } = fields;

  // Initialize form values from JSON fields
  const initialValues = formdata.reduce(
    (acc, field) => ({ ...acc, [field.name]: "" }),
    {},
  );
  initialValues.status = "1";

  const { values, errors, handleChange, setErrors, setValues } = useFormChange(
    initialValues,
    GrandLoanRules,
  );

  const { data: employeeOptions, loading: loadingEmployees } =
    useFetchAllData(GetEmployeeList);

  // Calculate repayment values in UI
  useEffect(() => {
    const principal = parseFloat(values.total_amount);
    const interest = parseFloat(values.interest_percentage);
    const months = parseInt(values.installment_period_month, 10);

    if (
      Number.isNaN(principal) ||
      Number.isNaN(interest) ||
      Number.isNaN(months) ||
      months <= 0
    ) {
      setValues((prev) => {
        if (!prev.repayment_total && !prev.installment) return prev;
        return { ...prev, repayment_total: "", installment: "" };
      });
      return;
    }

    const repaymentTotal = (principal + (principal * interest) / 100).toFixed(2);
    const installment = (Number(repaymentTotal) / months).toFixed(2);

    setValues((prev) => {
      if (
        prev.repayment_total === repaymentTotal &&
        prev.installment === installment
      ) {
        return prev;
      }
      return { ...prev, repayment_total: repaymentTotal, installment };
    });
  }, [
    values.total_amount,
    values.interest_percentage,
    values.installment_period_month,
    setValues,
  ]);

  // Form submit
  const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
    async (formValues) => {
      return await Create({ data: buildPayload(formValues) });
    },
    (vals) => {
      const err = validateForm(vals, GrandLoanRules);
      setErrors(err);
      return err;
    },
    () => navigate(-1),
    "Loan granted successfully",
  );

  return (
    <>
      {(loadingSubmit || loadingEmployees) && <Spinner isSpinner={true} />}
      <AddGrandLoanForm
        values={values}
        errors={errors}
        handleChange={handleChange}
        handleSubmit={(e) => handleSubmit(e, values)}
        loading={loadingSubmit}
        employeeOptions={employeeOptions}
      />
    </>
  );
};

export default AddGrandLoan;
